// ticket-dag-validator.ts
//
// Kiểm tra tính hợp lệ của đồ thị ticket TRƯỚC khi supervisor dispatch bất kỳ
// ticket nào (spec §5.1). Thuần: không I/O, không đọc mission-store.
//
// Bốn luật, theo thứ tự phát hiện (dừng ở lỗi đầu tiên — reject là reject,
// không cần liệt kê hết):
//   1. mọi `depends_on` phải trỏ tới ticket có thật trong danh sách;
//   2. đồ thị phải acyclic;
//   3. mỗi ticket kind="implement" phải có ticket kind="testcase" cùng
//      `slice_id` đứng TRƯỚC nó (test-first: viết ca test rồi mới code);
//   4. mỗi slice phải có "verification recipe của lát" = ít nhất một ticket
//      kind="test". Trường `verification_recipe` từng ticket đã `.min(1)` ở
//      schema, nên khoảng trống duy nhất còn lại là slice không có ticket test.
//
// Phase 09 (R-25): planner không còn phát tên tạm dạng `tmp:` (grep 0 hit
// trong pack) nên `resolvePlannerTempNames` mất producer thật — xoá cùng
// phase này. Luật 1-2 (findStructuralDagError) giờ được đường ghi ticket
// (ticket-file-persistence.ts) gọi TRƯỚC khi flush file, chặn cứng cả lô
// proposal có vòng/dep treo; luật 3-4 (findPolicyDagWarnings) chỉ cảnh báo
// (D2) — trả mảng, không chặn ticket hợp lệ theo thói quen hiện tại.
import type { Ticket } from "./schemas/ticket-schema.js";

export type TicketDagValidation = { valid: true } | { valid: false; reason: string };

function findUnknownDependency(tickets: Ticket[]): string | undefined {
  const known = new Set(tickets.map((t) => t.id));
  for (const ticket of tickets) {
    for (const dep of ticket.depends_on) {
      if (!known.has(dep)) {
        return `ticket ${ticket.id} depends on unknown ticket id "${dep}"`;
      }
    }
  }
  return undefined;
}

// DFS 3 màu: white (chưa thăm) → grey (đang trong stack) → black (xong). Gặp
// lại node grey ⇒ có cycle, và stack hiện tại chính là chu trình để báo cáo.
function findCycle(tickets: Ticket[]): string | undefined {
  const byId = new Map(tickets.map((t) => [t.id, t]));
  const state = new Map<string, "grey" | "black">();
  const stack: string[] = [];

  const walk = (id: string): string[] | undefined => {
    const mark = state.get(id);
    if (mark === "black") return undefined;
    if (mark === "grey") return [...stack.slice(stack.indexOf(id)), id];

    state.set(id, "grey");
    stack.push(id);
    for (const dep of byId.get(id)?.depends_on ?? []) {
      if (!byId.has(dep)) continue; // luật unknown-dependency lo việc này
      const cycle = walk(dep);
      if (cycle) return cycle;
    }
    stack.pop();
    state.set(id, "black");
    return undefined;
  };

  for (const ticket of tickets) {
    const cycle = walk(ticket.id);
    if (cycle) return `dependency cycle detected: ${cycle.join(" -> ")}`;
  }
  return undefined;
}

// "Đứng trước" = testcase cùng slice là tổ tiên (transitive depends_on) của
// implement, HOẶC — khi planner không khai cạnh — chỉ cần TỒN TẠI trong cùng
// slice, vì scheduler đã áp thứ tự kind testcase → implement → test trong lát
// (xem scheduler.ts KIND_ORDER). Thiếu cả hai vế mới là vi phạm test-first.
function ancestorsOf(id: string, byId: Map<string, Ticket>): Set<string> {
  const seen = new Set<string>();
  const queue = [...(byId.get(id)?.depends_on ?? [])];
  while (queue.length > 0) {
    const current = queue.pop();
    if (current === undefined || seen.has(current)) continue;
    seen.add(current);
    queue.push(...(byId.get(current)?.depends_on ?? []));
  }
  return seen;
}

function findImplementWithoutTestcase(tickets: Ticket[]): string | undefined {
  const byId = new Map(tickets.map((t) => [t.id, t]));
  for (const ticket of tickets) {
    if (ticket.kind !== "implement") continue;
    const ancestors = ancestorsOf(ticket.id, byId);
    const sliceTestcases = tickets.filter(
      (other) => other.kind === "testcase" && other.slice_id === ticket.slice_id,
    );
    // Ưu tiên bằng chứng mạnh (cạnh depends_on); không có cạnh thì chấp nhận
    // đồng-slice, nhưng KHÔNG có testcase nào trong slice là reject.
    const covered =
      sliceTestcases.some((other) => ancestors.has(other.id)) || sliceTestcases.length > 0;
    if (!covered) {
      return `implement ticket ${ticket.id} (slice ${ticket.slice_id}) has no preceding testcase ticket in its slice`;
    }
  }
  return undefined;
}

function findSliceWithoutVerificationRecipe(tickets: Ticket[]): string | undefined {
  const sliceIds: string[] = [];
  for (const ticket of tickets) {
    if (!sliceIds.includes(ticket.slice_id)) sliceIds.push(ticket.slice_id);
  }
  for (const sliceId of sliceIds) {
    const hasTest = tickets.some((t) => t.slice_id === sliceId && t.kind === "test");
    if (!hasTest) {
      return `slice ${sliceId} has no verification recipe: no ticket of kind "test" covers it`;
    }
  }
  return undefined;
}

/** Chạy đủ 4 luật; trả lý do đầu tiên tìm được. */
export function validateTicketDag(tickets: Ticket[]): TicketDagValidation {
  const reason =
    findUnknownDependency(tickets) ??
    findCycle(tickets) ??
    findImplementWithoutTestcase(tickets) ??
    findSliceWithoutVerificationRecipe(tickets);
  return reason === undefined ? { valid: true } : { valid: false, reason };
}

/**
 * Luật 1-2 (cấu trúc, D2 chặn cứng): dep không tồn tại HOẶC vòng. Gọi TRƯỚC
 * khi flush 1 lô proposal ra file (ticket-file-persistence.ts §applyBatch…)
 * — reject sớm ở đây có nghĩa 0 file ghi cho lô đó.
 */
export function findStructuralDagError(tickets: Ticket[]): string | undefined {
  return findUnknownDependency(tickets) ?? findCycle(tickets);
}

/**
 * Luật 3-4 (chính sách test-first, D2 chỉ cảnh báo): trả TOÀN BỘ vi phạm tìm
 * được (không dừng ở lỗi đầu, khác `validateTicketDag`) vì đây là notify, không
 * phải reject — người vận hành muốn thấy hết một lượt, không phải sửa-chạy-lại
 * từng dòng.
 */
export function findPolicyDagWarnings(tickets: Ticket[]): string[] {
  const warnings: string[] = [];
  const byId = new Map(tickets.map((t) => [t.id, t]));
  for (const ticket of tickets) {
    if (ticket.kind !== "implement") continue;
    const ancestors = ancestorsOf(ticket.id, byId);
    const sliceTestcases = tickets.filter(
      (other) => other.kind === "testcase" && other.slice_id === ticket.slice_id,
    );
    const covered =
      sliceTestcases.some((other) => ancestors.has(other.id)) || sliceTestcases.length > 0;
    if (!covered) {
      warnings.push(
        `implement ticket ${ticket.id} (slice ${ticket.slice_id}) has no preceding testcase ticket in its slice`,
      );
    }
  }
  const sliceIds: string[] = [];
  for (const ticket of tickets) {
    if (!sliceIds.includes(ticket.slice_id)) sliceIds.push(ticket.slice_id);
  }
  for (const sliceId of sliceIds) {
    const hasTest = tickets.some((t) => t.slice_id === sliceId && t.kind === "test");
    if (!hasTest) {
      warnings.push(
        `slice ${sliceId} has no verification recipe: no ticket of kind "test" covers it`,
      );
    }
  }
  return warnings;
}
